// Fichas técnicas y planilla HACCP exportables. Catering → Resumen → Fichas y HACCP.
// Documentos que el contrato institucional exige y que aquí se arman desde datos
// que ya existen: (1) ficha técnica por plato (ingredientes/gramaje/alérgenos/
// aporte/PCC), (2) planilla HACCP del día (control de cocción por batch, conforme
// S/N, zona de peligro, acción correctiva). Ambos imprimibles.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileCheck, ClipboardCheck, Printer, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, money } from './shared';

interface RecetaOpt { id: string; nombre: string; tipo: string }
interface Ficha {
  empresa: { nombre: string; ruc: string };
  receta: { nombre: string; tipo: string; categoria: string | null; porciones_std: number; rendimiento_cantidad: number; rendimiento_unidad: string; version: number; elaborado_por: string | null; instrucciones: string | null; temp_min_c: number | null; duracion_min: number; es_dieta_especial: boolean; estacion: string | null };
  ingredientes: { nombre: string; cantidad: number; unidad: string; merma_cant: number; notas: string | null; es_subreceta: boolean }[];
  alergenos: string[]; costo_porcion: number;
  nutricion: { cobertura_pct: number; por_porcion: { energia_kcal: number; proteina_g: number; grasa_g: number; carbohidrato_g: number; fibra_g: number; sodio_mg: number } };
}
interface HaccpBatch {
  codigo: string; receta: string; estacion: string | null; equipo: string | null; porciones: number; estado: string;
  es_pcc: boolean; limite_c: number | null; temperatura_c: number | null; cumple_pcc: boolean | null;
  iniciado_at: string | null; listo_at: string | null; min_peligro: number | null; peligro_conforme: boolean | null;
  responsable: string | null; accion_correctiva: string | null;
}
interface Haccp { empresa: { nombre: string; ruc: string }; fecha: string; orden: string | null; batches: HaccpBatch[]; resumen: { batches: number; con_pcc: number; no_conformes: number } }

const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export function HaccpCatering() {
  const [tab, setTab] = useState<'ficha' | 'haccp'>('ficha');
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap gap-1 print:hidden">
        {([['ficha', 'Ficha técnica', FileCheck], ['haccp', 'Planilla HACCP', ClipboardCheck]] as const).map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {tab === 'ficha' ? <TabFicha /> : <TabHaccp />}
    </div>
  );
}

function TabFicha() {
  const [recetaId, setRecetaId] = useState('');
  const { data: recetas = [] } = useQuery<RecetaOpt[]>({ queryKey: ['cat-recetas-opt'], queryFn: () => catRpc<RecetaOpt[]>('recetas_listar', { _tipo: null }) });
  const { data: f } = useQuery<Ficha>({ queryKey: ['cat-ficha', recetaId], enabled: !!recetaId, queryFn: () => catRpc<Ficha>('cat_ficha_tecnica', { _receta_id: recetaId }) });

  return (
    <Card>
      <CardHeader className="pb-2 print:hidden">
        <CardTitle className="text-base">Ficha técnica de plato/receta</CardTitle>
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <select className={`${fieldCls} w-72`} value={recetaId} onChange={(e) => setRecetaId(e.target.value)}>
            <option value="">Elige una receta…</option>
            {recetas.map((r) => <option key={r.id} value={r.id}>{r.tipo === 'plato' ? '🍽 ' : '• '}{r.nombre}</option>)}
          </select>
          {f && <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" /> Imprimir</Button>}
        </div>
      </CardHeader>
      <CardContent>
        {!f ? <p className="text-sm text-muted-foreground">Selecciona una receta para ver su ficha técnica.</p> : (
          <div className="mx-auto max-w-3xl space-y-3 text-sm">
            <div className="flex items-start justify-between border-b pb-2">
              <div><p className="text-lg font-bold">{f.receta.nombre}</p>
                <p className="text-xs text-muted-foreground">{f.empresa.nombre} · RUC {f.empresa.ruc} · Ficha técnica v{f.receta.version}</p></div>
              <div className="text-right text-xs">
                <p>{f.receta.tipo === 'plato' ? 'Plato ensamblado' : 'Receta base'}{f.receta.categoria ? ` · ${f.receta.categoria}` : ''}</p>
                <p>Porciones/batch: <b>{num(f.receta.porciones_std, 0)}</b></p>
                {f.receta.rendimiento_cantidad > 0 && <p>Rendimiento: {num(f.receta.rendimiento_cantidad, 2)} {f.receta.rendimiento_unidad}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Energía / porción</p><p className="font-mono font-semibold">{f.nutricion.por_porcion.energia_kcal} kcal</p></div>
              <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Proteína · Grasa · CHO</p><p className="font-mono">{f.nutricion.por_porcion.proteina_g} · {f.nutricion.por_porcion.grasa_g} · {f.nutricion.por_porcion.carbohidrato_g} g</p></div>
              <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Sodio · Fibra</p><p className="font-mono">{f.nutricion.por_porcion.sodio_mg} mg · {f.nutricion.por_porcion.fibra_g} g</p></div>
              <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Costo / porción</p><p className="font-mono">{money(f.costo_porcion, 4)}</p></div>
            </div>
            {f.nutricion.cobertura_pct < 100 && <p className="text-xs text-amber-600">Aporte con cobertura {f.nutricion.cobertura_pct}% de la MP (parcial).</p>}

            <div>
              <p className="pb-1 font-medium">Ingredientes (por batch de {num(f.receta.porciones_std, 0)} porciones)</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Componente</th><th className="py-1 pr-2 text-right">Cantidad</th><th className="py-1 pr-2">Unidad</th><th className="py-1 pr-2 text-right">Merma</th></tr></thead>
                <tbody>
                  {f.ingredientes.map((i, k) => (
                    <tr key={k} className="border-b last:border-0">
                      <td className="py-1 pr-2">{i.nombre}{i.es_subreceta && <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px]">subreceta</span>}{i.notas ? <span className="block text-xs text-muted-foreground">{i.notas}</span> : null}</td>
                      <td className="py-1 pr-2 text-right font-mono">{num(i.cantidad, 3)}</td>
                      <td className="py-1 pr-2">{i.unidad}</td>
                      <td className="py-1 pr-2 text-right font-mono">{i.merma_cant ? num(i.merma_cant, 3) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-4">
              <p><b>Alérgenos:</b> {f.alergenos.length ? <span className="text-red-600">{f.alergenos.join(', ')}</span> : 'ninguno declarado'}</p>
              {f.receta.temp_min_c != null && <p><b>PCC cocción:</b> ≥ {f.receta.temp_min_c} °C</p>}
              {f.receta.estacion && <p><b>Estación:</b> {f.receta.estacion} · {f.receta.duracion_min} min</p>}
              {f.receta.es_dieta_especial && <p className="text-amber-600">Dieta especial</p>}
            </div>
            {f.receta.instrucciones && <div><p className="font-medium">Preparación</p><p className="whitespace-pre-wrap text-sm text-muted-foreground">{f.receta.instrucciones}</p></div>}
            {f.receta.elaborado_por && <p className="text-xs text-muted-foreground">Elaborado por: {f.receta.elaborado_por}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TabHaccp() {
  const [fecha, setFecha] = useState(isoHoy());
  const { data: h } = useQuery<Haccp>({ queryKey: ['cat-haccp', fecha], queryFn: () => catRpc<Haccp>('cat_haccp_dia', { _fecha: fecha }) });
  return (
    <Card>
      <CardHeader className="pb-2 print:hidden">
        <CardTitle className="text-base">Planilla HACCP / APPCC del día</CardTitle>
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          {h?.orden && <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" /> Imprimir</Button>}
        </div>
      </CardHeader>
      <CardContent>
        {!h || !h.orden ? <p className="text-sm text-muted-foreground">No hay orden de producción para esta fecha.</p> : (
          <div className="space-y-3 text-sm">
            <div className="flex items-start justify-between border-b pb-2">
              <div><p className="text-lg font-bold">Control HACCP de producción</p>
                <p className="text-xs text-muted-foreground">{h.empresa.nombre} · RUC {h.empresa.ruc} · {h.fecha} · Orden {h.orden}</p></div>
              <div className="text-right text-xs">
                <p>{h.resumen.batches} tandas · {h.resumen.con_pcc} con PCC</p>
                <p className={h.resumen.no_conformes > 0 ? 'font-semibold text-red-600' : 'text-green-700 dark:text-green-400'}>
                  {h.resumen.no_conformes > 0 ? `${h.resumen.no_conformes} no conformes` : 'Todas conformes'}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">Tanda</th><th className="py-1 pr-2">Preparación</th><th className="py-1 pr-2">Estación</th>
                  <th className="py-1 pr-2 text-right">PCC (°C)</th><th className="py-1 pr-2 text-center">Conforme</th>
                  <th className="py-1 pr-2 text-right">Z. peligro</th><th className="py-1 pr-2">Responsable</th><th className="py-1 pr-2">Acción correctiva</th>
                </tr></thead>
                <tbody>
                  {h.batches.map((b) => {
                    const noConforme = (b.es_pcc && b.cumple_pcc === false) || b.peligro_conforme === false;
                    return (
                      <tr key={b.codigo} className={`border-b last:border-0 ${noConforme ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                        <td className="py-1 pr-2 font-mono text-xs">{b.codigo}</td>
                        <td className="py-1 pr-2">{b.receta}</td>
                        <td className="py-1 pr-2 text-xs">{b.estacion ?? '—'}</td>
                        <td className="py-1 pr-2 text-right font-mono">{b.es_pcc ? `${b.temperatura_c ?? '—'} / ≥${b.limite_c}` : '—'}</td>
                        <td className="py-1 pr-2 text-center">
                          {!b.es_pcc ? <span className="text-muted-foreground">n/a</span>
                            : b.cumple_pcc ? <CheckCircle2 className="inline h-4 w-4 text-green-600" />
                            : <AlertTriangle className="inline h-4 w-4 text-red-600" />}
                        </td>
                        <td className={`py-1 pr-2 text-right font-mono ${b.peligro_conforme === false ? 'text-red-600' : ''}`}>{b.min_peligro != null ? `${b.min_peligro} min` : '—'}</td>
                        <td className="py-1 pr-2 text-xs">{b.responsable ?? '—'}</td>
                        <td className="py-1 pr-2 text-xs text-red-700 dark:text-red-400">{b.accion_correctiva ?? ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">Límites: proteínas ≥ 74 °C, otros ≥ 63 °C (cocción). Zona de peligro 5–60 °C: máx. 4 h (240 min) desde que la tanda queda lista. Firma del responsable de calidad: ____________________</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
